import { useState, useEffect, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
// import icons
import { ArrowUpRight, Paperclip, MapPin } from "lucide-react";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
// import data
import { DataFlat, DataKeys } from '../../types';
// import components
import { UnitBadge } from "./Badges";
import { ImageZoom } from "./ImageFormat";
import { TextArrow, TextBulletList } from "./TextFormat";
// import function
import { LucideIconDynamic } from "../../functions/DisplayFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import context
import { useLang } from '../Context';
import { SlideInAnim } from "../tools/Animations";


interface CardProps {
    data: DataKeys;
};
interface ImageCardProps extends CardProps {
    imagePath?: string;
};
interface ProductCardProps extends CardProps {
    imagePath?: string;
    haveTag?: boolean;
}
interface ToolCardProps extends CardProps {
    isBulletList?: boolean;
    width: number;
    height: number;
};
interface ToolCardProps2 extends CardProps {
    isBulletList?: boolean;
    width: number;
    height: number;
    darkMode?: boolean;
    imagePath: string;
};
interface ToolCardProps3 extends CardProps {
    isBulletList?: boolean;
    width: number | string;
    height: number | string;
    darkMode?: boolean;
    imagePath: string;
};
interface PositionsCardProps extends CardProps {
    link: string;
};
interface NewsBlogCardProps extends CardProps {
    imagePath: string;
    link: string;
};

export function ProductCard({ data, imagePath, haveTag } : ProductCardProps) {
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [link, setLink] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            const src = require("../../" + imagePath + data["image"]);
            setImage(src);
            setTitle(data["title"][lang]);
            setText(data["text"][lang]);
            setTags(data["tags"]);
            let fixedLink = data["link"];
            if (lang === "Zh") {
                if (fixedLink.startsWith("/")) {
                    setLink("/tw" + fixedLink);
                } else {
                    setLink(fixedLink);
                };
            } else if (lang === "Jp") {
                if (fixedLink.startsWith("/")) {
                    setLink("/jp" + fixedLink);
                } else {
                    setLink(fixedLink);
                };
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);

    let showBadges = tags.map((i) => {
        return (
            <UnitBadge label={i} />
        )
    });

    return (
        <Link to={link} className="group w-full h-full grid grid-flow-row auto-rows-min gap-2 cursor-pointer relative hover:text-teal-500 m-auto">
            <ImageZoom image={ image } />
            <TextArrow label={ title } />
            <p className="text-left text-grey-400 text-sm">{ text }</p>
            { haveTag &&
                <div className="flex flex-wrap gap-2 mt-2">
                    { showBadges }
                </div>
            }
        </Link>
    )
};

export function ProductCardNoLink({ data, imagePath, haveTag } : ProductCardProps) {
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            const src = require("../../" + imagePath + data["image"]);
            setImage(src);
            setTitle(data["title"][lang]);
            setText(data["text"][lang]);
            setTags(data["tags"]);
        };
    }, [data, lang]);

    let showBadges = tags.map((i) => {
        return (
            <UnitBadge label={i} />
        )
    });

    return (
        <div className="group w-full h-full grid grid-flow-row auto-rows-min gap-2 relative m-auto">
            <ImageZoom image={ image } />
            <p className="text-left font-bold text-grey-800">{ title }</p>
            <p className="text-left text-grey-400 text-sm">{ text }</p>
            { haveTag &&
                <div className="flex flex-wrap gap-2 mt-2">
                    { showBadges }
                </div>
            }
        </div>
    )
};

export function SingleProductCard({ data, imagePath, haveTag } : ProductCardProps) {
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [link, setLink] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            const src = require("../../" + imagePath + data["image"]);
            setImage(src);
            setTitle(data["title"][lang]);
            setText(data["text"][lang]);
            setTags(data["tags"]);
            let fixedLink = data["link"];
            if (lang === "Zh") {
                setLink("/tw" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);

    let showBadges = tags.map((i) => {
        return (
            <UnitBadge label={i} />
        )
    });

    return (
        <Link to={link} className="group w-full h-full lg:grid lg:grid-cols-2 lg:grid-rows-1 lg:gap-8 sm:flex sm:flex-col sm:gap-2 cursor-pointer relative hover:text-teal-500 m-auto">
            <ImageZoom image={ image } />
            <div className="m-auto">
                <TextArrow label={ title } />
                <p className="text-left text-grey-400 text-sm">{ text }</p>
                { haveTag &&
                    <div className="flex flex-wrap gap-2 mt-3">
                        { showBadges }
                    </div>
                }
            </div>
        </Link>
    )
};


export function ToolCard3({ data, isBulletList, width, height, darkMode, imagePath } : ToolCardProps3) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");

    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [subtitle, setSubtitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [bulletList, setBulletList] = useState<string[]>([]);
    const [link, setLink] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);

            const src = require("../../" + imagePath + data["image"]);
            setImage(src);

            setTitle(data["title"][lang]);
            setSubtitle(data["subtitle"]?.[lang] ?? "");
            if (isBulletList) {
                setBulletList(data["text"][lang]);
            } else {
                setText(data["text"][lang]);
            };
            let fixedLink = data["link"];
            if (lang === "Zh") {
                setLink("/tw" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);

    const finalWidth = width ?? 300;
    const finalHeight = height ?? 330;


    return (
        <Link
        className="relative flex w-full items-center justify-between rounded-2xl
                    bg-white/60 backdrop-blur-md
                    border-[2px] border-transparent
                    hover:bg-white transition-colors duration-300 cursor-pointer group h-full"

            style={{
                width: typeof finalWidth === "string" ? finalWidth : `${finalWidth}px`,
                minHeight: typeof finalHeight === "string" ? undefined : `${finalHeight}px`,
                }}
            to={link}
        >
            <div className="flex gap-4 p-6 h-full w-full items-center ">
            <img src={image} alt="Custom Icon" width="40" height="40" />

            <div className="flex flex-col justify-start gap-3 w-full h-full">
            {/* title + arrow */}
            <div className="flex flex-row justify-between items-start">
                <p className="text-left text-[16px] font-bold text-grey-800 leading-snug whitespace-pre-line ">
                {title}
                </p>
                <ArrowUpRight
                className="shrink-0 font-bold stroke-[2.5] text-black "
                size={20}
                />
            </div>

            {/* subtitle */}
            {subtitle && (
                <p className="text-left text-[12px] font-semibold text-black g">
                {subtitle}
                </p>
            )}

            {/* bullet list */}
            {isBulletList ? (
                <TextBulletList
                list={bulletList}
                textStyle="text-[12px] text-grey-400 "
                />
            ) : (
                <p className="text-[12px] text-grey-400 ">{text}</p>
            )}
            </div>
            </div>

        </Link>
    )
};

ToolCard2.defaultProps = {
    width: 300,
    height: 330
};
export function ToolCard2({ data, isBulletList, width, height, darkMode, imagePath } : ToolCardProps2) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");

    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [subtitle, setSubtitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [bulletList, setBulletList] = useState<string[]>([]);
    const [link, setLink] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);

            const src = require("../../" + imagePath + data["image"]);
            setImage(src);

            setTitle(data["title"][lang]);
            setSubtitle(data["subtitle"]?.[lang] ?? "");
            if (isBulletList) {
                setBulletList(data["text"][lang]);
            } else {
                setText(data["text"][lang]);
            };
            let fixedLink = data["link"];
            if (lang === "Zh") {
                setLink("/tw" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);


    return (
        <Link
            className="relative flex w-full items-center justify-between rounded-2xl
                        border-[2px] border-transparent
                        hover:border-teal-500 hover:bg-white hover:text-teal-500 hover:shadow-back
                        transition-colors duration-300 cursor-pointer group"

            style={{
                width: `${width}px`,
                minHeight: `${height}px`,
                height: `${height}px`,
                backgroundColor: "rgba(248, 249, 250, 1)",
              }}
            to={link}
        >
            {/* <LucideIconDynamic name={ icon } width={112} height={112} /> */}

            <img src={image} alt="Custom Icon" width="112" height="112" />

            {/* <div className="grid grid-flow-row auto-rows-auto p-6 pl-0 gap-3">
                <p className="text-left text-lg font-bold text-grey-800 group-hover:text-teal-500">{ title }</p>
                { isBulletList
                    ? <TextBulletList list={bulletList} textStyle="text-sm text-grey-400 group-hover:text-teal-500" />
                    : <p className="text-left text-sm text-grey-400 group-hover:text-teal-500">{ text }</p>
                }
            </div> */}
                {/* <div className="flex items-center w-fit space-x-2">
                  <p className={`text-black text-teal-500 font-bold group-hover:text-teal-500 ${darkMode && "group-hover:text-white"}`}>Learn More</p>
                  <FontAwesomeIcon className={`${darkMode && "group-hover:text-white"}`} icon={faArrowRight} />
                </div> */}


            <div className="flex flex-col text-left flex-grow p-6 pl-1 gap-3">
            {/* title + arrow */}
            <div className="flex flex-row justify-between items-start">
                <p className="text-left text-lg font-bold text-grey-800 leading-snug whitespace-pre-line group-hover:text-teal-500">
                {title}
                </p>
                <ArrowUpRight
                className="font-bold stroke-[2.5] text-black group-hover:text-teal-500"
                size={20}
                />
            </div>

            {/* subtitle */}
            {subtitle && (
                <p className="text-sm font-semibold text-black group-hover:text-teal-500">
                {subtitle}
                </p>
            )}

            {/* bullet list */}
            {isBulletList ? (
                <TextBulletList
                list={bulletList}
                textStyle="text-sm text-grey-400 group-hover:text-teal-500"
                />
            ) : (
                <p className="text-sm text-grey-400 group-hover:text-teal-500">{text}</p>
            )}
            </div>

            {/* <div className="flex items-center absolute w-fit bottom-0 p-6 space-x-2">
                <p className={`text-black text-teal-500 font-bold group-hover:text-teal-500 ${darkMode && "group-hover:text-white"}`}>Learn More</p>
                <FontAwesomeIcon className={`${darkMode && "group-hover:text-white"}`} icon={faArrowRight} />
            </div> */}


        </Link>
    )
};


ToolCard.defaultProps = {
    width: 300,
    height: 330
};
export function ToolCard({ data, isBulletList, width, height } : ToolCardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [bulletList, setBulletList] = useState<string[]>([]);
    const [link, setLink] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            setTitle(data["title"][lang]);
            if (isBulletList) {
                setBulletList(data["text"][lang]);
            } else {
                setText(data["text"][lang]);
            };
            let fixedLink = data["link"];
            if (lang === "Zh") {
                setLink("/tw" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);


    return (
        <Link className="relative min-w-[240px] h-fit grid grid-flow-row auto-rows-min gap-6 bg-white p-6 rounded border-2 border-white
                        group hover:border-teal-500 hover:text-teal-500 hover:shadow-back transition-all duration-300 cursor-pointer"
            style={{ width: `${width}px`, minHeight: `${height}px` }}
            to={link}
        >
            <LucideIconDynamic name={ icon } width={32} height={32} />
            <div className="grid grid-flow-row auto-rows-auto gap-2">
                <p className="text-left text-lg font-bold text-grey-800 group-hover:text-teal-500">{ title }</p>
                { isBulletList
                    ? <TextBulletList list={bulletList} textStyle="text-sm text-grey-400 group-hover:text-teal-500" />
                    : <p className="text-left text-sm text-grey-400 group-hover:text-teal-500">{ text }</p>
                }
            </div>
            <div className="absolute w-fit bottom-0 right-0 p-6">
                <ArrowUpRight size={25} />
            </div>
        </Link>
    )
};

export function ToolCardNoLink({ data, isBulletList, width, height } : ToolCardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [bulletList, setBulletList] = useState<string[]>([]);
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            setTitle(data["title"][lang]);
            if (isBulletList) {
                setBulletList(data["text"][lang]);
            } else {
                setText(data["text"][lang]);
            };
        };
    }, [data, lang]);

    return (
        <div className="relative min-w-[240px] h-fit grid grid-flow-row auto-rows-min gap-6 bg-white p-6 rounded border-2 border-white
                        group hover:border-teal-500 hover:text-teal-500 hover:shadow-back transition-all duration-300"
            style={{ width: `${width}px`, minHeight: `${height}px` }}>
            <LucideIconDynamic name={ icon } width={32} height={32} />
            <div className="grid grid-flow-row auto-rows-auto gap-2">
                <p className="text-left text-lg font-bold text-grey-800 group-hover:text-teal-500">{ title }</p>
                { isBulletList
                    ? <TextBulletList list={bulletList} textStyle="text-sm text-grey-400 group-hover:text-teal-500" />
                    : <p className="text-left text-sm text-grey-400 group-hover:text-teal-500">{ text }</p>
                }
            </div>
        </div>
    )
};


export function BusinessUnitCard({ data, imagePath } : ImageCardProps) {
    const [image, setImage] = useState<string>("/image/Logo_core.svg");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [link, setLink] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            const src = require("../../" + imagePath + data["image"]);
            setImage(src);
            setTitle(data["title"][lang]);
            setText(data["text"][lang]);
            let fixedLink = data["link"];
            if (lang === "Zh") {
                setLink("/tw/" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp/" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);

    return (
        <Link to={link} className="w-fit min-w-[250px] sm:w-[250px] h-fit min-h-[300px] grid grid-flow-row auto-rows-min gap-3 py-6 rounded-2xl m-auto
                                    group hover:bg-white transition-all duration-300 cursor-pointer">
            <img className="m-auto w-32" src={ image } alt="logo" />
            <div className="w-full h-min flex items-center justify-center gap-2 text-md">
                <p className="w-fit font-bold text-grey-800 group-hover:text-teal-500">{ title }</p>
                <ArrowUpRight className="group-hover:text-teal-500" size={25} />
            </div>
            <p className="text-sm text-grey-400 group-hover:text-teal-500">{ text }</p>
        </Link>
    )
};


export function MemberCard({ data } : CardProps) {
    const [image, setImage] = useState<string>("cylin.jpg");
    const [name, setName] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setImage(data["image"]);
            setName(data["name"][lang]);
            setTitle(data["title"][lang]);
            setDescription(data["description"][lang]);
        };
    }, [data, lang]);

    return (
        <div className="w-[280px] grid grid-flow-row auto-rows-min gap-4 p-3">
            <img className="h-[200px] rounded-2xl w-full object-cover object-center select-none" src={require("../../image/people/" + image)} />
            <p className="text-left text-lg font-semibold text-grey-800 whitespace-pre-line ">{ name }</p>
            <div className="text-sm flex flex-col gap-1">
                <p className="text-left text-grey-400 font-semibold whitespace-pre-line ">{ title }</p>
                <p className="text-left text-grey-400">{ description }</p>
            </div>
        </div>
    )
};


export function StepsCard({ data } : CardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [step, setStep] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            setStep(data["step"][lang]);
            setTitle(data["title"][lang]);
            setText(data["text"][lang])
        };
    }, [data, lang]);

    return (
        <div className="flex flex-col gap-4">
            <LucideIconDynamic name={ icon } width={32} height={32} />
            <p className="text-left text-lg font-bold text-grey-800">
                <span>{ step }:</span><br/>
                <span>{ title }</span>
            </p>
            <p className="text-left text-grey-400">{ text }</p>
        </div>
    )

};

export function StepsCard2({ data } : CardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            const src_image = require("../../" + "image/robotics/" + data["image"]);
            setImage(src_image);
            setTitle(data["title"][lang]);
            setText(data["text"][lang])
        };
    }, [data, lang]);

    return (
        <div className="flex flex-col items-center gap-4">
            {/* <LucideIconDynamic name={ icon } width={32} height={32} /> */}
            <img className="p-6" src={image} />
            <p className="text-center text-lg font-bold text-grey-800 min-h-[56px]">{title}</p>
            <p className="text-center text-grey-400">{ text }</p>
        </div>
    )

};


export function StepsCard3({ data } : CardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            const src_image = require("../../" + "image/drugomics/" + data["image"]);
            setImage(src_image);
            setTitle(data["title"][lang]);
            setText(data["text"][lang])
        };
    }, [data, lang]);

    return (
        <div className="flex flex-col items-center gap-4 h-full ">
            {/* <LucideIconDynamic name={ icon } width={32} height={32} /> */}
            <img className="p-4" src={image} />
            <div className="w-full flex flex-col text-center lg:text-left gap-4">
                <p className=" text-lg font-bold text-grey-800 min-h-[56px]">{title}</p>
                <p className=" text-grey-400">{ text }</p>
            </div>
        </div>
    )

};


export function StepsCardWithList({ data, imagePath } : ImageCardProps) {
    const [icon, setIcon] = useState<keyof typeof dynamicIconImports>("layers");
    const [image, setImage] = useState<string>("");
    const [step, setStep] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [list, setList] = useState<string[]>([]);
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            setIcon(data["icon"] as keyof typeof dynamicIconImports);
            const src_image = require("../../" + imagePath + data["image"]);
            setImage(src_image);
            setStep(data["step"][lang]);
            setTitle(data["title"][lang]);
            setText(data["text"][lang])
            setList(data["list"][lang])
        };
    }, [data, lang]);

    return (
        <div className="flex flex-col gap-4">
            {/* <LucideIconDynamic name={ icon } width={32} height={32} /> */}

            <img className="p-6" src={image} />

            <p className="text-left text-lg font-bold text-grey-800">
                <span>{ step }:</span><br/>
                <span>{ title }</span>
            </p>
            <p className="text-left text-grey-400">{ text }</p>
            <TextBulletList list={list} />
        </div>
    )

};

export function OtherServicesCard({ data, imagePath } : ImageCardProps) {
    const [image, setImage] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [link, setLink] = useState<string>("");
    const { lang } = useLang();

    useEffect(() => {
        if (data) {
            const src = require("../../" + imagePath + data["image"]);
            setImage(src);
            setTitle(data["title"][lang]);
            setText(data["text"][lang]);
            let fixedLink = data["absoluteLink"];
            if (lang === "Zh") {
                setLink("/tw" + fixedLink);
            } else if (lang === "Jp") {
                setLink("/jp" + fixedLink);
            } else {
                setLink(fixedLink)
            };
        };
    }, [data, lang]);

    return (
        <Link to={link} className="min-w-[240px] max-w-[460px] w-fit h-fit grid grid-cols-3 place-items-center gap-4 p-6 rounded-2xl m-auto
                        group hover:bg-white transition-all duration-300 cursor-pointer">
            <div className="col-span-1">
                <img className="w-full h-full" src={image} />
            </div>
            <div className="col-span-2 flex flex-col gap-2">
                <div className="w-fit flex items-center justify-center gap-2">
                    <p className="text-left text-lg font-semibold text-grey-800">{ title }</p>
                    <ArrowUpRight size={25} />
                </div>
                <p className="text-left text-sm text-grey-400">{ text }</p>
            </div>
        </Link>
    )
};


interface LeftBorderCardProps extends CardProps {
    isBulletList?: boolean;
};
export function LeftBorderCard({ data, isBulletList } : LeftBorderCardProps) {
    const { lang } = useLang();
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [bulletList, setBulletList] = useState<string[]>([]);

    useEffect(() => {
        if (data) {
            setTitle(data["title"][lang]);
            if (isBulletList) {
                setBulletList(data["text"][lang]);
            } else {
                setText(data["text"][lang]);
            };
        };
    }, [data, lang]);


    return (
        <div className="min-w-[160px] w-fit h-fit flex flex-col gap-2 border-l-4 border-teal-300 px-4 leading-6">
            { title && <p className="text-left text-lg font-semibold text-grey-800">{ title }</p> }
            { isBulletList
                ? <TextBulletList list={bulletList} textStyle="text-left text-sm text-grey-400" />
                : <p className="text-left text-sm text-grey-400">{ text }</p>
            }
        </div>
    )
};


export function PositionCard({ data, link } : PositionsCardProps) {
    const [title, setTitle] = useState<string>("");
    const [location, setLocation] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [url, setUrl] = useState<string>("");
    const { lang } = useLang();


    useEffect(() => {
        if (data) {
            setTitle(data["Role"][lang]);
            setLocation(data["Location"][lang]);
            setText(data["Description"][lang]);
        };
    }, [data, lang]);

    useEffect(() => {
        if (lang === "Zh") {
            setUrl("/tw" + link);
        } else if (lang === "Jp") {
            setUrl("/jp" + link);
        } else {
            setUrl(link)
        };
    }, [link, lang]);


    return (
        <Link className="relative min-w-[240px] h-fit grid grid-flow-row auto-rows-min gap-6 bg-white p-6 rounded border-2 border-white
                        group hover:border-teal-500 hover:text-teal-500 hover:shadow-back transition-all duration-300 cursor-pointer"
            to={url}
        >
            <Paperclip width={32} height={32} />
            <div className="grid grid-flow-row auto-rows-auto gap-2 pb-6">
                <p className="text-left text-lg font-bold text-grey-800 group-hover:text-teal-500">{ title }</p>
                <div className="flex gap-2">
                    <MapPin className="text-grey-400 group-hover:text-teal-500" size={20} />
                    <p className="text-left text-sm text-grey-400 group-hover:text-teal-500">{ location }</p>
                </div>
                <p className="text-left text-sm text-grey-400 group-hover:text-teal-500">{ text }</p>
            </div>
            <div className="absolute w-fit bottom-0 right-0 p-6">
                <ArrowUpRight size={25} />
            </div>
        </Link>
    )
};


export function BlogCard({ data, imagePath, link } : NewsBlogCardProps) {
    const { lang } = useLang();

    const image =
        data["image"] !== ""
            ? require("../../" + imagePath + data["image"])
            : require("../../" + imagePath + "lackImg.png");
    const title = data["title"][lang] as string;
    const author = data["author"][lang] as string;
    const date = data["date"][lang] as string;
    const tags = data["tags"] as string[];
    const url =
        lang === "Zh" ? "/tw" + link : lang === "Jp" ? "/jp" + link : link;

    let showBadges = tags.map((i) => {
        return (
            <UnitBadge label={i} />
        )
    });

    return (
        <Link to={url} className="group w-full h-full grid grid-flow-row auto-rows-min gap-2 cursor-pointer relative hover:text-teal-500 m-auto">
            <ImageZoom image={ image } />
            <TextArrow label={ title } />
            <div className="flex flex-wrap gap-2 justify-between mt-2">
                <div className="flex flex-col">
                    <p className="text-left text-sm font-medium">{ author }</p>
                    <p className="text-left text-sm text-grey-400">{ date }</p>
                </div>
                <div className="h-fit flex items-center justify-center">
                    { showBadges }
                </div>
            </div>
        </Link>
    )
};

export function MoreNewsCard({ data, imagePath, link } : NewsBlogCardProps) {
    const { lang } = useLang();

    const image =
        data["image"] !== ""
            ? require("../../" + imagePath + data["image"])
            : require("../../" + imagePath + "lackImg.png");
    const title = data["title"][lang] as string;
    const author = data["author"][lang] as string;
    const date = data["date"][lang] as string;
    const url =
        lang === "Zh" ? "/tw" + link : lang === "Jp" ? "/jp" + link : link;

    return (
        <Link to={url} className="group w-full h-full grid grid-flow-row auto-rows-min gap-2 cursor-pointer relative hover:text-teal-500 m-auto">
            <ImageZoom image={ image } />
            <TextArrow label={ title } />
            <p className="text-left text-sm text-grey-400">{ date } | { author }</p>
        </Link>
    )
};


function extractNewsCardDate(dateString: string) {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
        return { day: "", month: "", year: "" };
    }

    return {
        day: date.getDate(),
        month: date.toLocaleString("en-US", { month: "short" }),
        year: date.getFullYear(),
    };
}

export function NewsCard({ data, imagePath, link } : NewsBlogCardProps) {
    const { lang } = useLang();

    const image =
        data["image"] !== ""
            ? require("../../" + imagePath + data["image"])
            : require("../../" + imagePath + "lackImg.png");
    const author = data["author"][lang] as string;
    const title = data["title"][lang] as string;
    const date = extractNewsCardDate(data["date"]["En"] as string);
    const url =
        lang === "Zh" ? "/tw" + link : lang === "Jp" ? "/jp" + link : link;

    return (
        <Link to={url} className="group w-full flex lg:flex-row sm:flex-col gap-6 py-8 px-6 cursor-pointer hover:bg-teal-400">
            <div className="grid grid-flow-col auto-cols-max gap-6">
                <div className="w-fit flex flex-col gap-4">
                    <p className="text-xs text-nowrap group-hover:text-white">{ date["month"] } { date["year"] }</p>
                    <p className="text-xl font-semibold group-hover:text-white">{ date["day"] }</p>
                </div>
                <img className="w-[200px] h-[200px] object-cover bg-center flex-shrink-0" src={ image } />
            </div>
            <div className="w-full flex flex-col gap-4">
                <p className="text-left text-xs font-medium group-hover:text-white">{ author }</p>
                <div className="w-full h-max flex lg:flex-row sm:flex-col justify-between gap-2">
                    <p className="text-left text-lg font-semibold text-grey-800 group-hover:text-white">{ title }</p>
                    <ArrowUpRight className="min-w-7 group-hover:text-white" size={24} />
                </div>
            </div>
        </Link>
    )
};

interface RobotProductCardProps {
    data: DataFlat;
    imagePath: string;
}

export function RobotProductCard({ data, imagePath }: RobotProductCardProps) {
    const { lang } = useLang();
    const [image, setImage] = useState<string>("");

    useEffect(() => {
        if (data && data["image"]) {
            try {
                const src = require("../../" + imagePath + data["image"]);
                setImage(src);
            } catch {
                setImage("");
            }
        }
    }, [data, imagePath]);

    if (!data) return null;

    const title = data["title"] ? data["title"][lang] : "";
    const text = data["text"] ? data["text"][lang] : [];

    return (
        <div className="flex flex-col gap-4">
            {/* Image */}
            {image && (
                <img
                    className="w-full h-auto object-cover"
                    src={image}
                    alt={title}
                />
            )}

            {/* Text Content */}
            <div className="flex flex-col gap-4 text-left">
                {title && (
                    <p className="text-lg font-bold text-left text-grey-800">{title}</p>
                )}

                {text && Array.isArray(text) && text.length > 0 && (
                    <div className="text-left">
                        {text.map((paragraph: string, index: number) => (
                            <p key={index} className="text-base text-grey-400 text-left mb-2 last:mb-0">
                                {paragraph}
                            </p>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};